import { useRouter } from 'next/router';
import { useCappsApplicationsWebApiHandler_acceptApplicationInvitation } from '@zg-rentals/ts-capps-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { useEffect } from 'react';
import { TallHeightLoadingMask } from '../../styles/css-components';
import { getEditRoute } from '../../constants/urls';
import { STEP_IDS } from '../../constants/wizard';
import { Toast, useToast } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';
import { REQUESTING_CLIENT } from '../../constants/api';
import { getZgBrand } from '../../constants/brands';

const GENERIC_ERROR_MESSAGE = 'We couldn’t add you to this application. Try again.';

function getErrorMessage(err: unknown): string {
  if (!DatarouterError.is(err)) {
    return GENERIC_ERROR_MESSAGE;
  }
  if (err.code === 'WRONG_ZILLOW_ACCOUNT') {
    return `Wrong Zillow account: ${err.message}`;
  }
  if (err.code === 'INVITE_DELETED') {
    return err.message;
  }
  return GENERIC_ERROR_MESSAGE;
}

type Props = {
  listingAlias: string;
  applicationId?: string;
  inviteId?: string;
  version?: string;
};

export function AcceptInvite({ listingAlias, applicationId, inviteId, version }: Props) {
  const router = useRouter();
  const { enqueueToast } = useToast();
  const { utm_source, utm_medium, utm_guid, utm_campaign } = router.query;
  const utmSource = typeof utm_source === 'string' ? utm_source : '';
  const utmMedium = typeof utm_medium === 'string' ? utm_medium : '';
  const utmGuid = typeof utm_guid === 'string' ? utm_guid : '';
  const utmCampaign = typeof utm_campaign === 'string' ? utm_campaign : '';
  const referrerBrand = getZgBrand(utmSource);

  const acceptInvitationMutation = useCappsApplicationsWebApiHandler_acceptApplicationInvitation({
    meta: { context: { api: 'cappsApplications', details: 'acceptApplicationInvitation' } },
  });

  async function acceptInvite() {
    if (inviteId) {
      try {
        const response = await acceptInvitationMutation.mutateAsync({
          postData: {
            applicationId,
            listingAlias,
            invitationId: inviteId,
            version,
            requestingClient: REQUESTING_CLIENT,
            utmSource,
            utmMedium,
            utmGuid,
            utmCampaign,
            referrerBrand,
          },
        });
        const createdApplicationId = response.applicationId;
        if (createdApplicationId) {
          router.push(getEditRoute({ applicationId: createdApplicationId, stepId: STEP_IDS.LEGAL_NAME }));
        }
      } catch (err) {
        logError({
          error: err,
          context: 'AcceptInvite - acceptInvitationMutation error',
        });
        enqueueToast(<Toast tone="critical">{getErrorMessage(err)}</Toast>);
        router.push('/');
      }
    }
  }

  useEffect(() => {
    if (listingAlias && inviteId) {
      acceptInvite();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [listingAlias, inviteId]);

  return (
    <TallHeightLoadingMask>
      <></>
    </TallHeightLoadingMask>
  );
}
